import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { FormattedMessage } from 'react-intl';
import BackArrow from 'assets/back-arrow.svg';
import ThemeStore from 'stories/ThemeStore';
import { Container, Section, Title } from '../shared/styled';
import { BalanceDisplay } from '../components/BalanceDisplay';
import { WithdrawalHistory } from './components/WithdrawalHistory';
import { WithdrawForm } from 'components/Finance/WithdrawFunds/components/forms/WithdrawForm';
import { Transaction, generateTransactionId } from '../components/transactionTypes';
import { WithdrawFormData } from './types';
import {DEFAULT_FORM_DATA} from "components/Finance/WithdrawFunds/constants";

interface WithdrawFundsProps {
    onBack: () => void;
}

export * from './types';

export const WithdrawFunds: React.FC<WithdrawFundsProps> = ({ onBack }) => {
    const [formData, setFormData] = useState<WithdrawFormData>(DEFAULT_FORM_DATA);

    const [transactions, setTransactions] = useState<Transaction[]>([]);
    const theme = ThemeStore.activeTheme;

    // Load transactions from localStorage
    useEffect(() => {
        const saved = localStorage.getItem('withdrawal_transactions');
        if (saved) {
            try {
                const parsed = JSON.parse(saved);
                setTransactions(parsed.map((t: any) => ({ ...t, date: new Date(t.date) })));
            } catch (error) {
                console.error('Failed to load withdrawal transactions:', error);
            }
        }
    }, []);

    // Save transactions to localStorage
    useEffect(() => {
        if (transactions.length > 0) {
            localStorage.setItem('withdrawal_transactions', JSON.stringify(transactions));
        }
    }, [transactions]);

    const handleFormChange = (updates: Partial<WithdrawFormData>) => {
        setFormData(prev => ({ ...prev, ...updates }));
    };

    const handleSubmit = () => {
        let details = '';
        if (formData.withdrawType === 'business') {
            details = `Withdrawal to business account (${formData.businessName})`;
        } else if (formData.withdrawalMethod === 'card') {
            details = `Withdrawal to card ending ${formData.cardNumber.slice(-4)}`;
        } else {
            details = "Withdrawal to an individual's bank account";
        }

        const newTransaction: Transaction = {
            id: generateTransactionId(),
            date: new Date(),
            type: 'withdrawal',
            paymentMethod: formData.withdrawalMethod === 'card' ? 'stripe' : 'stripe',
            amount: parseFloat(formData.amount),
            status: 'pending',
            details,
            cardLast4: formData.withdrawalMethod === 'card' ? formData.cardNumber.slice(-4) : '2456',
        };

        setTransactions(prev => [newTransaction, ...prev]);
        
        // Reset form
        setFormData(DEFAULT_FORM_DATA);

        console.log('Withdrawal request submitted:', newTransaction);
    };

    return (
        <WithdrawContainer>
            <LeftSection>
                <Section>
                    <BackButton onClick={onBack}>
                        <BackArrow color={theme.palette.text.breadcrumbs} />
                        <FormattedMessage id="Finance.Withdraw.Back" defaultMessage="Financial Overview" />
                    </BackButton>

                    <Title>
                        <FormattedMessage id="Finance.Withdraw.Title" defaultMessage="Withdraw Funds" />
                    </Title>

                    <WithdrawForm 
                        formData={formData}
                        onFormChange={handleFormChange}
                        onSubmit={handleSubmit}
                    />
                </Section>

                <Section style={{ height: '100%' }}>
                    <WithdrawalHistory transactions={transactions} />
                </Section>
            </LeftSection>

            <Section style={{ height: 'auto' }}>
                <BalanceDisplay balance={30345.90} />
            </Section>
        </WithdrawContainer>
    );
};

const WithdrawContainer = styled(Container)`
    flex-direction: row;
    gap: 22px;
    @media (max-width: 1280px) {
        flex-direction: column;
        gap: 12px;
    }
`;

const LeftSection = styled.div`
    display: flex;
    flex-direction: column;
    gap: 18px;
    flex: 1;
    min-height: calc(100vh - 146px);
    @media (max-width: 1024px) {
        min-height: auto;
        gap: 12px;
    }
`;

const BackButton = styled.button`
    display: flex;
    align-items: center;
    gap: 8px;
    background: none;
    border: none;
    color: #00000080;
    font-size: 13px;
    font-weight: 400;
    cursor: pointer;
    padding: 0;
    transition: opacity 0.2s ease;
    font-family: SF Pro Display, sans-serif;
    &:hover { opacity: 0.7; }
    svg {
        width: 16px;
        height: 16px;
    }
    @media (max-width: 768px) {
        font-size: 12px;
        svg {
            width: 14px;
            height: 14px;
        }
    }
`;
